import axios from 'axios';
import { ladeToken, loescheAuth } from 'core/features/auth/authApi';

/**
 * Axios-Client fuer die Aktien-API (`/api/aktien`).
 *
 * Relative baseURL: Im Produktivbetrieb leitet nginx `/api/aktien` an den
 * aktien-service weiter. Alle Endpunkte antworten im ApiResponse-Wrapper
 * `{ success, message, data }`; die Helfer geben direkt `data` zurueck.
 */
const aktienApi = axios.create({
  baseURL: '/api/aktien',
  // Live-Abrufe grosser Indizes (z.B. S&P 500) koennen serverseitig mehrere
  // OnVista-Seiten laden -> grosszuegiges Timeout.
  timeout: 30000,
});

// Haengt das JWT (falls vorhanden) an jede Anfrage; Depot/Watchlists sind geschuetzt.
aktienApi.interceptors.request.use((config) => {
  const token = ladeToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Bei 401 (Token fehlt/abgelaufen) abmelden und zur Login-Seite leiten.
aktienApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      loescheAuth();
      if (window.location.pathname !== '/login') {
        window.location.assign('/login');
      }
    }
    return Promise.reject(error);
  }
);

const unwrap = (response) => response.data?.data;

// ── Indizes ─────────────────────────────────────────────
// Liste der konfigurierten Indizes (Reiter).
export const fetchIndizesApi = () => aktienApi.get('/indizes').then(unwrap);

// Aktuelle Kurse eines Index LIVE von OnVista (kein DB-Zugriff, kein Speichern).
export const fetchLiveAktienByIndexApi = (indexName) =>
  aktienApi.get(`/indizes/${encodeURIComponent(indexName)}/live`).then(unwrap);

// ── Aktien aus der Datenbank ────────────────────────────
// Uebersicht aller gespeicherten Aktien (Name + letzter Kurs).
export const fetchAllAktienUebersichtApi = () =>
  aktienApi.get('/uebersicht').then(unwrap);

// Detail einer Aktie inkl. Tageswerten und Jahreswerten.
export const fetchAktieDetailApi = (isin) =>
  aktienApi.get(`/${encodeURIComponent(isin)}`).then(unwrap);

// Aktueller Live-Kurs einer Aktie direkt von OnVista (ohne DB, kann 1-3s dauern).
export const fetchAktieLiveApi = (isin) =>
  aktienApi.get(`/${encodeURIComponent(isin)}/live`).then(unwrap);

// Fertig berechnete Analyse (Chart/Fundamental/Dividende/Risiko/Gesamturteil) aus DB-Daten.
export const fetchAktieAnalyseApi = (isin) =>
  aktienApi.get(`/${encodeURIComponent(isin)}/analyse`).then(unwrap);

// Laedt die historischen Tageswerte erneut von OnVista; ergaenzt fehlende Werte in der DB.
export const reloadHistoryApi = (isin) =>
  aktienApi.post(`/load/history/${encodeURIComponent(isin)}`).then(unwrap);

// Richtet eine neue Aktie per ISIN oder WKN vollstaendig ein (OnVista + komplette
// History). Laeuft serverseitig ueber mehrere gedrosselte OnVista-Calls -> langes Timeout.
export const neueAktieApi = (kennung) =>
  aktienApi.post('/load/neu', { kennung }, { timeout: 90000 }).then(unwrap);

// Aktien mit auffaelliger Tagesbewegung der letzten Tage (Default: 7 Tage, > 10 %).
export const fetchSchwankungenApi = () =>
  aktienApi.get('/schwankungen').then(unwrap);

// Daten-Check-Logs (History-Differenzen + Kursspruenge) fuer den Debug-Bereich.
export const fetchDatenCheckLogsApi = (limit = 200) =>
  aktienApi.get('/daten-check-logs', { params: { limit } }).then(unwrap);

// ── Watchlists ──────────────────────────────────────────
export const fetchWatchlistsApi = () => aktienApi.get('/watchlists').then(unwrap);

export const createWatchlistApi = (name) =>
  aktienApi.post('/watchlists', { name }).then(unwrap);

export const deleteWatchlistApi = (uuid) =>
  aktienApi.delete(`/watchlists/${uuid}`).then(() => uuid);

export const addAktieApi = (uuid, aktie) =>
  aktienApi.post(`/watchlists/${uuid}/aktien`, aktie).then(unwrap);

export const removeAktieApi = (uuid, isin) =>
  aktienApi.delete(`/watchlists/${uuid}/aktien/${encodeURIComponent(isin)}`).then(unwrap);

// ── Depot ───────────────────────────────────────────────
// Die CRUD-Endpunkte liefern die schnelle Basisansicht { positionen, gruppen }
// (nur Kaufdaten). Kurs/Urteil/Dividende/Perf werden pro Position nachgeladen.
export const fetchDepotApi = () => aktienApi.get('/depot').then(unwrap);

// Angereicherte Werte einer einzelnen Position (Kurs, Urteil, Dividende, Perf).
export const fetchDepotPositionWerteApi = (isin) =>
  aktienApi.get(`/depot/${encodeURIComponent(isin)}/werte`).then(unwrap);

// Position hinzufuegen (kennung = Name/ISIN/WKN). Stoesst serverseitig den
// Tiefen-History-Abruf an.
export const addDepotPositionApi = (position) =>
  aktienApi.post('/depot', position).then(unwrap);

export const deleteDepotPositionApi = (isin) =>
  aktienApi.delete(`/depot/${encodeURIComponent(isin)}`).then(unwrap);

// Nachkauf/Verkauf einer Position (typ = KAUF | VERKAUF).
export const addDepotTransaktionApi = (isin, transaktion) =>
  aktienApi.post(`/depot/${encodeURIComponent(isin)}/transaktionen`, transaktion).then(unwrap);

// Position einer Gruppe zuordnen (gruppeUuid = null -> keine Gruppe).
export const setDepotGruppeApi = (isin, gruppeUuid) =>
  aktienApi.put(`/depot/${encodeURIComponent(isin)}/gruppe`, { gruppeUuid }).then(unwrap);

// ── Depot-Gruppen ───────────────────────────────────────
export const createDepotGruppeApi = (name) =>
  aktienApi.post('/depot/gruppen', { name }).then(unwrap);

export const renameDepotGruppeApi = (uuid, name) =>
  aktienApi.put(`/depot/gruppen/${uuid}`, { name }).then(unwrap);

export const deleteDepotGruppeApi = (uuid) =>
  aktienApi.delete(`/depot/gruppen/${uuid}`).then(unwrap);

export const reorderDepotGruppenApi = (reihenfolge) =>
  aktienApi.put('/depot/gruppen/reihenfolge', reihenfolge).then(unwrap);

export default aktienApi;
